import React, {useState} from "react";
import {T, useTranslate} from "@tolgee/react";
import {Button, Typography} from "@material-ui/core";
import {copyToClipboardAsync} from "tools/common/copyToClipboardAsync";
import downloadBlobFile from "tools/file/downloadBlobFile";
import Alert from "view/components/Alert";
import FlexBox from "view/components/Layout/FlexBox";
import CopyIcon from "view/images/CopyIcon";
import DownloadIcon from "@material-ui/icons/GetApp";
import {blue} from "@material-ui/core/colors";
import WarningIcon from "@material-ui/icons/ReportProblemOutlined";
import useStyles from "./styles";

type Props = {
  codes: string[];
  onDone: () => void;
};

const COPY_RESET_DELAY_MS = 2000;

const RecoveryCodes: React.FC<Props> = ({codes, onDone}) => {
  const classes = useStyles();
  const {t} = useTranslate();
  const [copied, setCopied] = useState(false);
  const [copyFailed, setCopyFailed] = useState(false);

  const codesText = codes.join("\n");

  const handleCopyAll = async () => {
    const succeeded = await copyToClipboardAsync(async () => codesText);
    setCopied(succeeded);
    setCopyFailed(!succeeded);
    setTimeout(() => setCopied(false), COPY_RESET_DELAY_MS);
  };

  const handleDownload = () => {
    downloadBlobFile(
      new Blob([codesText], {type: "text/plain"}),
      "ffair-recovery-codes.txt"
    );
  };

  return (
    <div className={classes.wrapper}>
      <FlexBox column gap={2} className={classes.codesBoxWrapper}>
        <Typography variant="h5" className={classes.label}>
          <T keyName="common.two_factor.recovery.title" />
        </Typography>
        <div className={classes.codesBox}>
          {codes.map((code) => (
            <div key={code} className={classes.codeWrapper}>
              <Typography className={classes.code} variant="h4">
                {code}
              </Typography>
            </div>
          ))}
        </div>
        <FlexBox fullWidth justifyContent="flex-end" gap={2}>
          <Button
            variant="text"
            color="primary"
            onClick={handleCopyAll}
            startIcon={<CopyIcon color={blue.A400} />}
          >
            {copied
              ? t("common.two_factor.recovery.copied")
              : t("common.two_factor.recovery.copy_all")}
          </Button>
          <Button
            variant="text"
            color="primary"
            onClick={handleDownload}
            startIcon={
              <DownloadIcon className={classes.icon} color="primary" />
            }
          >
            <T keyName="common.two_factor.recovery.download" />
          </Button>
        </FlexBox>
      </FlexBox>

      {copyFailed && (
        <Alert severity="error" fullWidth>
          <T keyName="common.two_factor.recovery.copy_failed" />
        </Alert>
      )}
      <Alert
        severity="warning"
        icon={<WarningIcon />}
        classes={{icon: classes.alertIcon}}
        className={classes.alert}
      >
        <T keyName="common.two_factor.recovery.helper" />
      </Alert>
    </div>
  );
};

export default RecoveryCodes;
